import { useState } from "react";
import Box from "@amzn/meridian/esm/components/box";
import Column from "@amzn/meridian/esm/components/column";
import Divider from "@amzn/meridian/esm/components/divider";
import Row from "@amzn/meridian/esm/components/row";
import Text from "@amzn/meridian/esm/components/text";
import Theme from "@amzn/meridian/esm/components/theme";
import Tile from "@amzn/meridian/esm/components/tile";
import { typographyH50FontWeight } from "@amzn/meridian-tokens/base/typography";
import { THEME_COLOR } from "../../styles/colors";
import {
  MISSING_VALUE,
  REGULAR_COLLABORATOR_MIN_MEETINGS,
} from "./whosWho.constants";
import { classifyWhosWhoInsights } from "./whosWho.utils";
import InsightSlot from "./InsightSlot";
import InviteeOverview from "./InviteeOverview";
import ProfilePhoto from "./ProfilePhoto";

const getMetBeforeLabel = (count) => count >= REGULAR_COLLABORATOR_MIN_MEETINGS
  ? "Regular collaborator"
  : count > 0
    ? `Met before ${count} ${count === 1 ? "time" : "times"}`
    : "First time meeting";

const HIERARCHY_NAME_TOKENS = {
  textB100FontWeight: typographyH50FontWeight,
  textColorPrimary: THEME_COLOR.CHARCOAL_GRAY,
};

const getRoleAndOrg = ({ role, org }) =>
  [role, org].filter(Boolean).join(", ") || MISSING_VALUE;

const InviteeCard = ({ person, isError, accentColor }) => {
  const [expanded, setExpanded] = useState(false);
  const isExternal = person.external;
  const canExpand = !isError && !isExternal && !person.isYou;
  const metBeforeLabel = getMetBeforeLabel(person.metBeforeCount);
  const { pinnedInsight, expandedInsights } = classifyWhosWhoInsights(person.insights);
  const supervisors = [...(person.supervisors || [])].reverse();

  const cardWidths = isError ? ["fill"] : ["fit", "fill"];

  const showErrorDetail = isError && !isExternal && !person.isYou;
  const toggleExpanded = () => setExpanded((value) => !value);

  return (
    <Tile accent={accentColor} accentPosition="start" spacingInset="300">
      <Row
        width="100%"
        widths={cardWidths}
        spacing="350"
        alignmentVertical="top"
      >
        {!isError && (
          <Column spacing="200" alignmentHorizontal="center">
            <ProfilePhoto
              alias={person.alias}
              initials={person.initials}
              hasPhoto={person.hasPhoto}
            />
            {!isExternal && person.alias && (
              <Text type="b100" color="secondary">{person.alias}</Text>
            )}
          </Column>
        )}
        <Column spacing="200" width="fill">
          <InviteeOverview
            person={person}
            isError={isError}
            canExpand={canExpand}
            pinnedInsight={pinnedInsight}
            expanded={expanded}
            onToggle={toggleExpanded}
          />

          {canExpand && expanded && (
            <Column spacing="200">
              <Box spacingInset="300 none">
                <Divider />
              </Box>
              <Text type="b100" color="secondary" tag="ul" listStyle="disc">
                <Text type="b100" color="secondary" tag="li">
                  {metBeforeLabel}
                </Text>
                {expandedInsights.map((insight, index) => (
                  <Text
                    key={`${insight.kind}-${index}`}
                    type="b100"
                    color="secondary"
                    tag="li"
                  >
                    <InsightSlot insight={insight} />
                  </Text>
                ))}
              </Text>
              {supervisors.length > 0 && (
                <Column spacing="100" spacingInset="300 none none none">
                  {supervisors.map((supervisor) => (
                    <Row
                      key={supervisor.alias}
                      widths={["fit", "fill"]}
                      spacing="100"
                      alignmentVertical="top"
                    >
                      <Text type="b100" color="secondary">└</Text>
                      <Column spacing="none">
                        <Theme tokens={HIERARCHY_NAME_TOKENS}>
                          <Text type="b100" color="primary">
                            {supervisor.name || supervisor.alias || MISSING_VALUE}
                          </Text>
                        </Theme>
                        <Text type="b100" color="secondary">
                          {getRoleAndOrg(supervisor)}
                        </Text>
                      </Column>
                    </Row>
                  ))}
                  <Row
                    widths={["fit", "fill"]}
                    spacing="100"
                    alignmentVertical="top"
                  >
                    <Text type="b100" color="secondary">▸</Text>
                    <Column spacing="none">
                      <Theme tokens={HIERARCHY_NAME_TOKENS}>
                        <Text type="b100" color="primary">
                          {person.name || person.alias || MISSING_VALUE}
                        </Text>
                      </Theme>
                      <Text type="b100" color="secondary">
                        {getRoleAndOrg(person)}
                      </Text>
                    </Column>
                  </Row>
                </Column>
              )}
            </Column>
          )}
          {showErrorDetail && (
            <Text type="b100" color="secondary">{MISSING_VALUE}</Text>
          )}
        </Column>
      </Row>
    </Tile>
  );
};

export default InviteeCard;
